import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  ChevronRight,
  Globe,
  Linkedin,
  ShieldCheck,
} from "lucide-react";

// Sections
import IndustrialHero from "./IndustrialHero";
import CorporateAbout from "./CorporateAbout";
import IndustryVerticals from "./IndustryVerticals";
import SubsidiaryEcosystem from "./SubsidiaryEcosystem";
import CorporateTimeline from "./CorporateTimeline";
import LeadershipLegacy from "./LeadershipLegacy";
import OperationalGallery from "./OperationalGallery";
import CertificationBar from "./CertificationBar";
import ContactPortal from "./ContactPortal";
import CorporateFooter from "./CorporateFooter";

import Logo from "../../Images/FocusLogoT.png"
import { Helmet } from "react-helmet";

const FocusGroupWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "unset";
  }, [isMenuOpen]);

  // ✅ Added HOME
  const navLinks = [
    { name: "HOME", href: "#home" },
    { name: "ABOUT", href: "#about" },
    { name: "INDUSTRIES", href: "#industries" },
    { name: "ECOSYSTEM", href: "#ecosystem" },
    { name: "TIMELINE", href: "#timeline" },
    { name: "LEADERSHIP", href: "#leadership" },
    { name: "CONTACT", href: "#contact" },
  ];

  return (

    <>
    
    <Helmet>

  <title>
    Focus NDT Group | Industrial Inspection | Focus Inspection Technology | FocusIn360 | EvalNDT
  </title>

  <meta
    name="description"
    content="
Focus NDT Group is a global industrial inspection and NDT services organization including Focus Inspection Technology, Focus NDT Innovations, FocusIn360 software and EvalNDT. We provide PAUT, TOFD, UT, RT, MT, PT, welding inspection, offshore inspection, refinery inspection and digital inspection solutions worldwide.
"
  />

  <meta
    name="keywords"
    content="
Focus NDT Group,
Focus Inspection Technology,
Focus NDT,
Focus Inspection,
FocusIn360,
EvalNDT,
NDT company,
industrial inspection,
PAUT,
TOFD,
UT testing,
RT testing,
MT PT testing,
inspection software,
oil and gas inspection,
shipyard inspection,
pipeline inspection,
asset integrity,
ndt india,
ndt worldwide,
inspection company
"
  />

  <link
    rel="canonical"
    href="https://focusndtgroup.com/"
  />

  {/* Open Graph */}

  <meta
    property="og:title"
    content="Focus NDT Group | Global Industrial Inspection & NDT Services"
  />

  <meta
    property="og:description"
    content="Focus NDT Group includes Focus Inspection Technology, FocusIn360, EvalNDT and global industrial inspection services."
  />

  <meta
    property="og:image"
    content="https://focusndtgroup.com/images/FocusLogoT.png"
  />

  <meta
    property="og:url"
    content="https://focusndtgroup.com/"
  />

  <meta property="og:type" content="website" />

  {/* Twitter */}

  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="Focus NDT Group"
  />

  <meta
    name="twitter:description"
    content="Industrial inspection, NDT services, inspection software, Focus Inspection Technology, FocusIn360, EvalNDT."
  />

  <meta
    name="twitter:image"
    content="https://focusndtgroup.com/images/FocusLogoT.png"
  />

</Helmet>
    <div className="min-h-screen bg-white text-slate-900 font-sans scroll-smooth">

      {/* ================= HEADER ================= */}

      <header className="bg-white/95 backdrop-blur-md border-b border-slate-100 sticky top-0 z-[100]">

        <nav className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">

          {/* LOGO */}

<a href="#home" className="flex items-center gap-3">

  <img
    src={Logo}
    alt="Focus NDT Group"
    className="h-10 w-auto object-contain"
    loading="eager"
  />

<div className="flex flex-col">
  <span className="text-xl font-black tracking-tight leading-none uppercase text-blue-800">
    FOCUS
  </span>
  <span className="text-[9px] tracking-[0.3em] text-orange-600 font-bold uppercase">
    NDT GROUP
  </span>
</div>

</a>

          {/* DESKTOP NAV */}

          <div className="hidden xl:flex items-center space-x-8 text-[11px] font-bold tracking-widest text-slate-500">

            {navLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="hover:text-orange-600 transition uppercase tracking-[0.2em]"
              >
                {item.name}
              </a>
            ))}

            <a
              href="#contact"
              className="bg-slate-950 text-white px-6 py-3 rounded-sm font-black hover:bg-orange-600 transition text-[10px]"
            >
              CONTACT HQ
            </a>

          </div>

          {/* MOBILE BUTTON */}

          <button
            className="xl:hidden p-2"
            onClick={() => setIsMenuOpen(true)}
          >
            <Menu size={28} />
          </button>

        </nav>

      </header>



      {/* ================= SIDEBAR ================= */}

      <div
        className={`fixed inset-0 z-[9999] transition ${
          isMenuOpen ? "visible" : "invisible"
        }`}
      >

        <div
          className={`absolute inset-0 bg-slate-950/80 ${
            isMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsMenuOpen(false)}
        />

        <div
          className={`absolute top-0 right-0 h-full w-[85%] max-w-[400px] bg-slate-950 transform transition ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >

          <div className="p-8 pt-12 flex flex-col h-full">

            <div className="flex justify-between mb-12">

              <span className="text-white font-black">
                MENU
              </span>

              <button onClick={() => setIsMenuOpen(false)}>
                <X size={30} className="text-white" />
              </button>

            </div>

            <div className="space-y-4">

              {navLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-3xl font-black text-white flex justify-between border-b border-white/10 py-2"
                >
                  {item.name}
                  <ChevronRight />
                </a>
              ))}

            </div>

<div className="mt-auto pt-10 border-t border-white/10 space-y-4">

  <div className="flex flex-col gap-3 text-sm">

    <a
      href="https://focusinspectiontechnology.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-white/80 hover:text-orange-400"
    >
      Focus Inspection Technology →
    </a>

    <a
      href="https://focusin360.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-white/80 hover:text-orange-400"
    >
      Focus NDT Innovations →
    </a>

    <a
      href="https://focusin360.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-white/80 hover:text-orange-400"
    >
      FocusIn360 Software →
    </a>

    <a
      href="https://evalndt.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-white/80 hover:text-orange-400"
    >
      EvalNDT Software →
    </a>

  </div>

  <p className="text-[10px] text-slate-600 mt-4">
    © 2026 Focus NDT Group
  </p>

</div>

          </div>

        </div>

      </div>



      {/* ================= MAIN ================= */}

      <main>

        <div id="home">
          <IndustrialHero />
        </div>

        <div id="about">
          <CorporateAbout />
        </div>

        <div id="industries">
          <IndustryVerticals />
        </div>

        <div id="ecosystem">
          <SubsidiaryEcosystem />
        </div>

        <div id="timeline">
          <CorporateTimeline />
        </div>

        <div id="leadership">
          <LeadershipLegacy />
        </div>

        <OperationalGallery />

        <CertificationBar />

        <div id="contact">
          <ContactPortal />
        </div>

      </main>



      {/* ================= FOOTER ================= */}

      <footer>
        <CorporateFooter />
      </footer>

    </div>
    </>
  );
};

export default FocusGroupWebsite;